import { useEffect, useRef, useState, type RefObject } from 'react';
import { formatCount } from '@/i18n/format';
import { formatCopy } from '@/i18n/ns';
import type libraryUploadCopy from '@/i18n/ns/library-upload';
import { uploadStatusText } from './library-rows';
import type { LibraryUpload, LibraryUploadStatus } from './upload-queue';
import type { LibraryDragPreview } from './use-library-drop';

type UploadCopy = (typeof libraryUploadCopy)['en'];

/**
 * ⌘U / Ctrl+U opens the file picker from anywhere in the Library: not from a dialog or a canvas over or beside it,
 * which keep the keys for themselves. On a Cyrillic or Greek layout the physical U key counts.
 */
export function useUploadShortcut(input: RefObject<HTMLInputElement | null>, enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    const open = (event: KeyboardEvent) => {
      if (event.defaultPrevented || !(event.metaKey || event.ctrlKey)) return;
      if (event.altKey || event.shiftKey) return;
      // The letter when the layout types a Latin one (Dvorak's U key types G); else the physical U key.
      const latin = /^[a-z]$/iu.test(event.key);
      if (latin ? event.key.toLowerCase() !== 'u' : event.code !== 'KeyU') return;
      if (event.target instanceof Element && event.target.closest('[role="dialog"], .ws-canvas'))
        return;
      event.preventDefault();
      input.current?.click();
    };
    window.addEventListener('keydown', open);
    return () => window.removeEventListener('keydown', open);
  }, [enabled, input]);
}

function change(
  upload: LibraryUpload,
  was: LibraryUploadStatus | undefined,
  copy: UploadCopy,
  locale: string,
) {
  const name = upload.name;
  if (upload.status === 'uploading' && was !== 'uploading')
    return formatCopy(copy.uploadingName, { name });
  if (upload.status === 'done') return formatCopy(copy.uploadedName, { name });
  if (upload.status === 'paused') return formatCopy(copy.pausedName, { name });
  if (upload.status === 'problem')
    return upload.problem?.kind === 'failed'
      ? uploadStatusText(upload, copy, locale)
      : `${name}: ${uploadStatusText(upload, copy, locale)}`;
  return '';
}

/**
 * What a polite live region says: an upload starting, finishing, pausing or failing, and the drop target opening.
 * Progress itself is on each row's progressbar.
 */
export function useUploadAnnouncement(
  uploads: readonly LibraryUpload[],
  dragging: LibraryDragPreview | null,
  copy: UploadCopy,
  locale: string,
) {
  const [message, setMessage] = useState('');
  const seen = useRef(new Map<string, LibraryUploadStatus>());
  useEffect(() => {
    const said: string[] = [];
    const next = new Map<string, LibraryUploadStatus>();
    for (const upload of [...uploads].reverse()) {
      const was = seen.current.get(upload.key);
      next.set(upload.key, upload.status);
      if (was === upload.status) continue;
      const line = change(upload, was, copy, locale);
      if (line) said.push(line);
    }
    seen.current = next;
    if (said.length) setMessage(said.join(' '));
  }, [uploads, copy, locale]);
  const dropCount = dragging?.count ?? 0;
  useEffect(() => {
    if (dropCount)
      setMessage(
        formatCopy(copy.dropTitle, { files: formatCount(copy.dropFiles, dropCount, locale) }),
      );
  }, [dropCount, copy, locale]);
  return message;
}
